import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  Building2, 
  Car, 
  Truck, 
  Landmark,
  ArrowRight,
  Check,
  Clock,
  DollarSign,
  Shield,
  Zap
} from 'lucide-react';

const audiences = [
  {
    id: 'healthcare',
    label: 'Healthcare Providers',
    icon: Building2,
    title: 'Transform Patient Transportation',
    description: 'Eliminate no-shows, reduce administrative burden, and improve patient outcomes with reliable, AI-coordinated transportation for hospitals, clinics, dialysis centers, and insurance companies.',
    benefits: [
      { icon: Clock, text: 'Reduce no-show rates by up to 40%' },
      { icon: DollarSign, text: 'Automated billing & claims processing' },
      { icon: Shield, text: 'HIPAA-compliant operations' },
      { icon: Zap, text: 'Real-time patient tracking' },
    ],
    cta: 'Schedule a Demo',
    ctaLink: '#contact',
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=2053&auto=format&fit=crop',
  },
  {
    id: 'drivers',
    label: 'Drivers',
    icon: Car,
    title: 'Maximize Your Earnings',
    description: 'Join the largest NEMT network with instant payouts, flexible scheduling, and AI-optimized routes that help you earn more while providing quality care to patients.',
    benefits: [
      { icon: DollarSign, text: 'Instant payouts after every trip' },
      { icon: Zap, text: 'AI-optimized route assignments' },
      { icon: Clock, text: 'Flexible scheduling on your terms' },
      { icon: Shield, text: 'Group insurance coverage available' },
    ],
    cta: 'Become a Driver',
    ctaLink: 'https://ovadispatch.com/driver',
    external: true,
    image: 'https://images.unsplash.com/photo-1764392961916-527f8a979c48?q=80&w=2000&auto=format&fit=crop',
  },
  {
    id: 'companies',
    label: 'Transportation Companies',
    icon: Truck,
    title: 'Scale Your Operations',
    description: 'Whether you operate 5 vehicles or 500, scale your NEMT operations with our comprehensive platform. Access more trips, streamline dispatch, automate compliance, and grow your fleet with AI-powered efficiency.',
    benefits: [
      { icon: Zap, text: 'Direct access to trip marketplace' },
      { icon: Shield, text: 'Centralized fleet management' },
      { icon: DollarSign, text: 'Automated payment reconciliation' },
      { icon: Clock, text: 'Real-time fleet tracking' },
    ],
    cta: 'Partner With Us',
    ctaLink: '#contact',
    image: 'https://images.pexels.com/photos/18442877/pexels-photo-18442877.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    id: 'government',
    label: 'Cities / Governments',
    icon: Landmark,
    title: 'Modernize Public Transit',
    description: 'Partner with Health and Human Services departments to implement efficient, accountable, and accessible transportation solutions for Medicaid beneficiaries and underserved communities.',
    benefits: [
      { icon: Shield, text: 'Complete audit trail & compliance' },
      { icon: DollarSign, text: 'Cost transparency & optimization' },
      { icon: Zap, text: 'ADA-compliant services' },
      { icon: Clock, text: 'Real-time performance reporting' },
    ],
    cta: 'Request RFP Info',
    ctaLink: '#contact',
    image: 'https://images.pexels.com/photos/29344111/pexels-photo-29344111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
];

export const AudienceSection = () => {
  const [activeTab, setActiveTab] = useState('healthcare');

  return (
    <section 
      id="solutions" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="audience-section"
    >
      {/* Background */}
      <div className="absolute inset-0 neural-bg opacity-20" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            Solutions
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Who This Is For
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            MedexOVA serves every stakeholder in the healthcare transportation ecosystem.
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap justify-center gap-2 mb-12 bg-transparent">
            {audiences.map((audience) => (
              <TabsTrigger
                key={audience.id}
                value={audience.id}
                className={`
                  flex items-center space-x-2 px-6 py-3 rounded-full
                  font-medium transition-all duration-300
                  data-[state=active]:bg-white data-[state=active]:text-black
                  data-[state=inactive]:bg-white/5 data-[state=inactive]:text-slate-400
                  data-[state=inactive]:hover:bg-white/10 data-[state=inactive]:hover:text-white
                  border border-transparent data-[state=active]:border-white/20
                `}
                data-testid={`tab-${audience.id}`}
              >
                <audience.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{audience.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {audiences.map((audience) => (
            <TabsContent 
              key={audience.id} 
              value={audience.id}
              className="mt-0"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
              >
                {/* Content */}
                <div className="order-2 lg:order-1">
                  <div className="inline-flex items-center space-x-2 mb-4">
                    <audience.icon className="w-5 h-5 text-neon-cyan" />
                    <span className="text-sm font-mono text-neon-cyan uppercase tracking-wider">
                      For {audience.label}
                    </span>
                  </div>
                  
                  <h3 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
                    {audience.title}
                  </h3>
                  
                  <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                    {audience.description}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {audience.benefits.map((benefit, i) => (
                      <div 
                        key={i}
                        className="flex items-start space-x-3 p-4 glass-card"
                      >
                        <benefit.icon className="w-5 h-5 text-neon-cyan flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-300">{benefit.text}</span>
                      </div>
                    ))}
                  </div>

                  {audience.external ? (
                    <a
                      href={audience.ctaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary inline-flex items-center"
                      data-testid={`cta-${audience.id}`}
                    >
                      {audience.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  ) : (
                    <a
                      href={audience.ctaLink}
                      className="btn-primary inline-flex items-center"
                      data-testid={`cta-${audience.id}`}
                    >
                      {audience.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  )}
                </div>

                {/* Image */}
                <div className="order-1 lg:order-2">
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                    <img
                      src={audience.image}
                      alt={audience.label}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-medex-bg/80 via-transparent to-transparent" />
                    
                    {/* Overlay badge */}
                    <div className="absolute bottom-4 left-4 glass px-4 py-2 rounded-full">
                      <span className="text-sm font-mono text-white">
                        {audience.id === 'healthcare' ? '500+ Providers' :
                         audience.id === 'drivers' ? '12,000+ Drivers' :
                         audience.id === 'companies' ? '800+ Partners' :
                         '50+ Cities'}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default AudienceSection;
